/**
 * Export utilities for talent data
 * Handles data extraction and formatting for Excel/CSV export
 */
import { getNoteTypeLabel } from './noteTypeUtils'
import {
  getPrimaryProfession,
  getDisplayProfessionName,
  getDepartmentName,
  getPositionName
} from './professionUtils'

/**
 * Mapping of column keys to display labels
 * Used for building headers and UI
 */
export const COLUMN_LABELS = {
  id: 'ID',
  name: 'Name',
  email: 'Email',
  telegram: 'Telegram',
  phone: 'Phone',
  department: 'Department',
  profession: 'Profession',
  position: 'Position',
  secondary_professions: 'Secondary Professions',
  status: 'Status',
  last_contact_date: 'Last Contact Date',
  link: 'Link',
  recruiter: 'Recruiter',
  talent_id: 'Talent ID',
  talent_type: 'Talent Type',
  other_contacts: 'Other Contacts',
  tools: 'Tools',
  responsibilities: 'Responsibilities',
  languages: 'Languages',
  location: 'Location',
  salary_expectations: 'Salary Expectations',
  salary_proposed: 'Proposed Salary',
  salary_shift: 'Shift',
  salary_rate: 'Rate',
  notes: 'Notes',
  interview_date: 'Interview Date',
  interview_time: 'Interview Time',
  start_work_date: 'Start Work Date',
  fired_date: 'Fired Date',
  left_date: 'Left Date',
  follow_up_date: 'Follow-up Date',
  created_at: 'Created At',
  status_updated_at: 'Status Changed At',
  status_change_reason: 'Status Change Reason',
  public_rate: 'Public Rate',
  public_slug: 'Public Slug',
  public_short_name: 'Public Short Name',
  public_seo_title: 'Public SEO Title',
  public_seo_description: 'Public SEO Description',
  last_company: 'Last Company',
  contacts: 'Contacts',
  media_portrait: 'Portrait',
  media_portfolio: 'Portfolio',
  media_youtube: 'YouTube',
  media_youtube_shorts: 'YouTube Shorts',
  media_resume: 'Resume',
  media_contract: 'Contract',
  media_audio: 'Audio',
  media_video: 'Video'
}

/**
 * Extract email from talent data
 * Priority: authUser.email > primary email contact > any email contact
 */
export const extractEmail = (talent) => {
  let emailValue = ''

  // Try to get email from authUser
  if (talent.authUser?.email) {
    emailValue = talent.authUser.email
  }

  // If no authUser email, search in contacts for email tool
  if (!emailValue && talent.contacts && talent.contacts.length > 0) {
    // Try primary email contact first
    const primaryEmail = talent.contacts.find(c =>
      c.is_primary && c.tool?.name?.toLowerCase().includes('email')
    )

    if (primaryEmail) {
      emailValue = typeof primaryEmail.value === 'object'
        ? (primaryEmail.value?.value || '')
        : (primaryEmail.value || '')
    } else {
      // Try any email contact
      const anyEmail = talent.contacts.find(c =>
        c.tool?.name?.toLowerCase().includes('email')
      )
      if (anyEmail) {
        emailValue = typeof anyEmail.value === 'object'
          ? (anyEmail.value?.value || '')
          : (anyEmail.value || '')
      }
    }
  }

  return formatValue(emailValue)
}

/**
 * Extract Telegram from talent data
 * Priority: primary telegram contact > any telegram contact
 */
export const extractTelegram = (talent) => {
  let telegramValue = ''

  if (talent.contacts && talent.contacts.length > 0) {
    // Try primary telegram contact first
    const primaryTelegram = talent.contacts.find(c =>
      c.is_primary && c.tool?.name?.toLowerCase().includes('telegram')
    )

    if (primaryTelegram) {
      telegramValue = typeof primaryTelegram.value === 'object'
        ? (primaryTelegram.value?.value || '')
        : (primaryTelegram.value || '')
    } else {
      // Try any telegram contact
      const anyTelegram = talent.contacts.find(c =>
        c.tool?.name?.toLowerCase().includes('telegram')
      )
      if (anyTelegram) {
        telegramValue = typeof anyTelegram.value === 'object'
          ? (anyTelegram.value?.value || '')
          : (anyTelegram.value || '')
      }
    }
  }

  return formatValue(telegramValue)
}

/**
 * Extract Phone from talent data
 * Priority: primary phone contact > any phone contact
 * Adds ' prefix for Excel to treat as text (prevents issues with spaces)
 */
export const extractPhone = (talent) => {
  let phoneValue = ''

  if (talent.contacts && talent.contacts.length > 0) {
    // Try primary phone contact first
    const primaryPhone = talent.contacts.find(c =>
      c.is_primary && (c.tool?.name?.toLowerCase().includes('phone') ||
                       c.tool?.name?.toLowerCase().includes('mobile') ||
                       c.tool?.name?.toLowerCase().includes('whatsapp'))
    )

    if (primaryPhone) {
      phoneValue = typeof primaryPhone.value === 'object'
        ? (primaryPhone.value?.value || '')
        : (primaryPhone.value || '')
    } else {
      // Try any phone contact
      const anyPhone = talent.contacts.find(c =>
        c.tool?.name?.toLowerCase().includes('phone') ||
        c.tool?.name?.toLowerCase().includes('mobile') ||
        c.tool?.name?.toLowerCase().includes('whatsapp')
      )
      if (anyPhone) {
        phoneValue = typeof anyPhone.value === 'object'
          ? (anyPhone.value?.value || '')
          : (anyPhone.value || '')
      }
    }
  }

  // Add ' prefix for Excel to treat as text
  return phoneValue ? `'${phoneValue}` : ''
}

/**
 * Extract gender from talent data
 */
export const extractGender = (talent) => {
  return talent.gender || ''
}

/**
 * Format value for export
 * Supports custom empty marker for null/undefined/empty values
 */
const formatValue = (value, options = {}) => {
  if (value === null || value === undefined || value === '') {
    return options.emptyMarker !== undefined ? options.emptyMarker : ''
  }
  return String(value)
}

/**
 * Extract profession from talent data
 * Priority: primary profession > first profession
 * Returns only profession name (department and position are separate columns)
 */
export const extractProfession = (talent) => {
  return getDisplayProfessionName(getPrimaryProfession(talent))
}

/**
 * Extract department from talent data
 * Priority: primary profession department > first profession department
 */
export const extractDepartment = (talent) => {
  return getDepartmentName(getPrimaryProfession(talent))
}

/**
 * Extract position from talent data
 * Priority: primary profession position > first profession position
 */
export const extractPosition = (talent) => {
  return getPositionName(getPrimaryProfession(talent))
}

/**
 * Extract secondary professions from talent data
 * Excludes whichever profession was used as primary (primary flag or first item)
 * Format: "Profession, Profession"
 */
export const extractSecondaryProfessions = (talent) => {
  if (!talent.professions || talent.professions.length === 0) return ''

  // Use the same primary selection logic as extractProfession
  const primaryProfession = getPrimaryProfession(talent)
  const secondaryProfessions = talent.professions.filter(p => p !== primaryProfession)

  return secondaryProfessions
    .map(prof => getDisplayProfessionName(prof))
    .filter(Boolean)
    .join(', ') || ''
}

/**
 * Extract other contacts (excluding email, phone, telegram) as comma-separated string
 * Format: "Tool Name: value, Tool Name: value"
 */
export const extractOtherContacts = (talent) => {
  if (!talent.contacts || talent.contacts.length === 0) return ''
  
  const excludedTypes = ['email', 'phone', 'mobile', 'telegram', 'whatsapp']
  
  return talent.contacts
    .filter(c => {
      const toolName = c.tool?.name?.toLowerCase() || ''
      return !excludedTypes.some(type => toolName.includes(type))
    })
    .map(c => {
      const toolName = c.tool?.name || 'Contact'
      const value = typeof c.value === 'object' ? c.value?.value : c.value
      return value ? `${toolName}: ${value}` : ''
    })
    .filter(Boolean)
    .join(', ') || ''
}

/**
 * Extract all contacts as newline-separated string
 * Format: "Tool Name: value" per line
 */
export const extractAllContacts = (talent) => {
  if (!talent.contacts || talent.contacts.length === 0) return ''

  return talent.contacts
    .map(c => {
      const toolName = c.tool?.name || 'Contact'
      const value = typeof c.value === 'object' ? c.value?.value : c.value
      return value ? `${toolName}: ${value}` : ''
    })
    .filter(Boolean)
    .join('\n')
}

/**
 * Extract tools as comma-separated string
 */
export const extractTools = (talent) => {
  return talent.tools?.map(t => t.tool?.name).filter(Boolean).join(', ') || ''
}

/**
 * Extract responsibilities as comma-separated string
 */
export const extractResponsibilities = (talent) => {
  return talent.responsibilities?.map(r => r.responsibility?.name).filter(Boolean).join(', ') || ''
}

/**
 * Extract languages with levels as comma-separated string
 */
export const extractLanguages = (talent) => {
  return talent.languages?.map(l => {
    const langName = l.language?.name || ''
    const level = l.level ? ` (${l.level.short_name || l.level.name})` : ''
    return langName + level
  }).join(', ') || ''
}

/**
 * Extract talent type from talent data
 */
export const extractTalentType = (talent) => {
  if (!talent.talent_type) {
    return ''
  }

  // Format talent type: capitalize first letter
  const type = talent.talent_type.toLowerCase()
  return type.charAt(0).toUpperCase() + type.slice(1)
}

/**
 * Extract location (country and city)
 */
export const extractLocation = (talent) => {
  return talent.country
    ? `${talent.country.name}${talent.city ? ', ' + talent.city.name : ''}`
    : ''
}

/**
 * Extract salary expectations (amount + currency)
 * Format: "5000.00 USD" or "5000.00"
 */
export const extractSalaryExpectations = (talent) => {
  if (!talent.salary?.expectations_amount) return ''
  const amount = parseFloat(talent.salary.expectations_amount).toFixed(2)
  const currency = (talent.salary.expectations_currency?.symbol || talent.salary.expectations_currency?.name || '').trim()
  return currency ? `${amount} ${currency}` : amount
}

/**
 * Extract proposed salary (amount + currency)
 * Format: "6000.00 USD" or "6000.00"
 */
export const extractSalaryProposed = (talent) => {
  if (!talent.salary?.proposed_salary_amount) return ''
  const amount = parseFloat(talent.salary.proposed_salary_amount).toFixed(2)
  const currency = (talent.salary.proposed_salary_currency?.symbol || talent.salary.proposed_salary_currency?.name || '').trim()
  return currency ? `${amount} ${currency}` : amount
}

/**
 * Extract shift info
 */
export const extractSalaryShift = (talent) => {
  if (!talent.salary?.shift) return ''
  const shift = talent.salary.shift
  const time = shift.start_time && shift.end_time
    ? ` (${shift.start_time.slice(0, 5)} - ${shift.end_time.slice(0, 5)})`
    : ''
  return `${shift.name || ''}${time}`.trim()
}

/**
 * Extract notes as pipe-separated string
 * Format: "[Type] Text | [Type] Text"
 */
export const extractNotes = (talent) => {
  if (!talent.notes || talent.notes.length === 0) return ''
  return talent.notes
    .filter(n => n.note)
    .map(n => {
      const typeLabel = n.note_type ? `[${getNoteTypeLabel(n.note_type)}] ` : ''
      const cleanNote = n.note.trim().replace(/\r?\n/g, ' ')
      return `${typeLabel}${cleanNote}`
    })
    .join('\n')
}

/**
 * Format date for export
 */
export const formatDateForExport = (dateString) => {
  if (!dateString) return ''
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  } catch {
    return ''
  }
}

/**
 * Extract a specific important date value by date_type
 */
const extractImportantDate = (talent, dateType) => {
  return talent.importantDates?.find(d => d.date_type === dateType)?.date_value || ''
}

/**
 * Extract date part only (YYYY-MM-DD portion)
 */
export const extractInterviewDate = (talent) => {
  const val = extractImportantDate(talent, 'interview')
  if (!val) return ''
  return formatDateForExport(val)
}

/**
 * Extract time part only (HH:MM) from interview datetime
 */
export const extractInterviewTime = (talent) => {
  const val = extractImportantDate(talent, 'interview')
  if (!val) return ''
  try {
    const d = new Date(val)
    const h = String(d.getHours()).padStart(2, '0')
    const m = String(d.getMinutes()).padStart(2, '0')
    return `${h}:${m}`
  } catch {
    return ''
  }
}

export const extractStartWorkDate = (talent) => {
  const val = extractImportantDate(talent, 'start_work')
  return val ? formatDateForExport(val) : ''
}

export const extractFiredDate = (talent) => {
  const val = extractImportantDate(talent, 'fired')
  return val ? formatDateForExport(val) : ''
}

export const extractLeftDate = (talent) => {
  const val = extractImportantDate(talent, 'left')
  return val ? formatDateForExport(val) : ''
}

export const extractFollowUpDate = (talent) => {
  const val = extractImportantDate(talent, 'follow_up')
  return val ? formatDateForExport(val) : ''
}

export const extractCreatedAt = (talent) => {
  return talent.created_at ? formatDateForExport(talent.created_at) : ''
}

export const extractStatusUpdatedAt = (talent) => {
  return talent.status_updated_at ? formatDateForExport(talent.status_updated_at) : ''
}

export const extractStatusChangeReason = (talent) => {
  return talent.current_status_change_reason || ''
}

export const extractPublicRate = (talent) => {
  return talent.publicSettings?.rate ? String(talent.publicSettings.rate) : ''
}

export const extractPublicSlug = (talent) => {
  return talent.publicSettings?.slug || ''
}

export const extractPublicShortName = (talent) => {
  return talent.publicSettings?.short_name || ''
}

export const extractPublicSeoTitle = (talent) => {
  return talent.publicSettings?.seo_title || ''
}

export const extractPublicSeoDescription = (talent) => {
  return talent.publicSettings?.seo_description || ''
}

/**
 * Extract all media items of a given media_type as pipe-separated string
 * URL entries: the URL itself; file entries: file_path (download URL)
 */
const extractMediaByType = (talent, mediaType) => {
  if (!talent.media || talent.media.length === 0) return ''
  return talent.media
    .filter(m => m.media_type === mediaType)
    .map(m => m.mime_type === 'text/url'
      ? m.file_path
      : (m.file_path || m.file_url || m.file_name || ''))
    .filter(Boolean)
    .join('\n')
}

export const extractLastCompany = (talent) => {
  if (!talent.cv || talent.cv.length === 0) return ''
  const experiences = talent.cv
    .filter(c => c.cv_type === 'experiences' && c.company_name)
    .sort((a, b) => {
      // Sort by start_date descending (most recent first); null end_date = current job
      if (!a.end_date && b.end_date) return -1
      if (a.end_date && !b.end_date) return 1
      return new Date(b.start_date || 0) - new Date(a.start_date || 0)
    })
  return experiences[0]?.company_name || ''
}

/**
 * Extract rate info
 */
export const extractSalaryRate = (talent) => {
  if (!talent.salary?.rate) return ''
  const rate = talent.salary.rate
  const details = rate.hours ? ` (${rate.hours}h)` : ''
  return `${rate.name || ''}${details}`.trim()
}

/**
 * Build row data based on selected columns
 * Supports custom column order and empty marker
 */
export const buildExportRow = (talent, exportColumns, userOptions, columnOrder = null, options = {}) => {
  const orderedKeys = columnOrder || Object.keys(exportColumns)
  const row = []
  const { emptyMarker } = options

  // Helper function to extract raw value by key
  const extractValue = (key) => {
    switch (key) {
      case 'id':
        return String(talent.id ?? '')
      case 'name':
        return `${talent.last_name || ''} ${talent.first_name || ''}`.trim()
      case 'email':
        return extractEmail(talent)
      case 'telegram':
        return extractTelegram(talent)
      case 'phone':
        return extractPhone(talent)
      case 'department':
        return extractDepartment(talent)
      case 'profession':
        return extractProfession(talent)
      case 'position':
        return extractPosition(talent)
      case 'secondary_professions':
        return extractSecondaryProfessions(talent)
      case 'status':
        return talent.currentStatus?.name || ''
      case 'last_contact_date': {
        // First try importantDates with type 'last_contact'
        const contactDate = talent.importantDates?.find(d => d.date_type === 'last_contact')
        if (contactDate) return formatDateForExport(contactDate.date_value)
        // Fallback: most recent system event
        const lastEvent = talent.events?.[0]
        return lastEvent ? formatDateForExport(lastEvent.occurred_at) : ''
      }
      case 'link':
        return talent.id ? `${window.location.origin}/talents/${talent.id}` : ''
      case 'recruiter': {
        // Use manager object directly on talent (available when fetched by ID)
        if (talent.manager?.name) return talent.manager.name
        // Fallback to userOptions lookup (for list-based data)
        const manager = userOptions.find(u => u.id === talent.manager_id)
        return manager ? (manager.name || manager.email) : ''
      }
      case 'talent_id':
        return String(talent.talent_id ?? '')
      case 'talent_type':
        return extractTalentType(talent)
      case 'contacts':
        return extractAllContacts(talent)
      case 'other_contacts':
        return extractOtherContacts(talent)
      case 'tools':
        return extractTools(talent)
      case 'responsibilities':
        return extractResponsibilities(talent)
      case 'languages':
        return extractLanguages(talent)
      case 'location':
        return extractLocation(talent)
      case 'salary_expectations':
        return extractSalaryExpectations(talent)
      case 'salary_proposed':
        return extractSalaryProposed(talent)
      case 'salary_shift':
        return extractSalaryShift(talent)
      case 'salary_rate':
        return extractSalaryRate(talent)
      case 'notes':
        return extractNotes(talent)
      case 'interview_date':
        return extractInterviewDate(talent)
      case 'interview_time':
        return extractInterviewTime(talent)
      case 'start_work_date':
        return extractStartWorkDate(talent)
      case 'fired_date':
        return extractFiredDate(talent)
      case 'left_date':
        return extractLeftDate(talent)
      case 'follow_up_date':
        return extractFollowUpDate(talent)
      case 'created_at':
        return extractCreatedAt(talent)
      case 'status_updated_at':
        return extractStatusUpdatedAt(talent)
      case 'status_change_reason':
        return extractStatusChangeReason(talent)
      case 'public_rate':
        return extractPublicRate(talent)
      case 'public_slug':
        return extractPublicSlug(talent)
      case 'public_short_name':
        return extractPublicShortName(talent)
      case 'public_seo_title':
        return extractPublicSeoTitle(talent)
      case 'public_seo_description':
        return extractPublicSeoDescription(talent)
      case 'last_company':
        return extractLastCompany(talent)
      case 'media_portrait':
        return extractMediaByType(talent, 'portrait')
      case 'media_portfolio':
        return extractMediaByType(talent, 'portfolio')
      case 'media_youtube':
        return extractMediaByType(talent, 'youtube')
      case 'media_youtube_shorts':
        return extractMediaByType(talent, 'youtube_shorts')
      case 'media_resume':
        return extractMediaByType(talent, 'resume')
      case 'media_contract':
        return extractMediaByType(talent, 'contract')
      case 'media_audio':
        return extractMediaByType(talent, 'audio')
      case 'media_video':
        return extractMediaByType(talent, 'video')
      default:
        // Return static value for custom columns
        return options.customValues?.[key] ?? ''
    }
  }

  orderedKeys.forEach(key => {
    if (exportColumns[key]) {
      const value = extractValue(key)
      // Apply emptyMarker consistently to all empty values
      row.push((!value && emptyMarker) ? emptyMarker : value)
    }
  })

  return row
}

/**
 * Build headers based on selected columns
 * Supports custom column order and custom column labels
 */
export const buildExportHeaders = (exportColumns, columnOrder = null, customLabels = {}) => {
  const orderedKeys = columnOrder || Object.keys(exportColumns)
  const headers = []

  orderedKeys.forEach(key => {
    if (exportColumns[key]) {
      headers.push(customLabels[key] || COLUMN_LABELS[key] || key)
    }
  })

  return headers
}

/**
 * Escape cell value for TSV format
 */
export const escapeTSVCell = (cell) => {
  const cellStr = String(cell || '')

  // Escape cells that contain tabs, newlines, or quotes
  if (cellStr.includes('\t') || cellStr.includes('\n') || cellStr.includes('"')) {
    return `"${cellStr.replace(/"/g, '""')}"`
  }

  return cellStr
}

/**
 * Convert data to TSV (Tab-Separated Values) format
 * Supports optional headers
 */
export const convertToTSV = (headers, rows, includeHeaders = true) => {
  const allRows = includeHeaders ? [headers, ...rows] : rows

  return allRows
    .map(row => row.map(escapeTSVCell).join('\t'))
    .join('\n')
}

/**
 * Main export function - prepares and exports talent data
 * Supports column order, empty marker, and optional headers
 */
export const exportTalentsToClipboard = async (
  talents,
  exportColumns,
  userOptions,
  options = {}
) => {
  const {
    columnOrder = null,
    includeHeaders = true,
    emptyMarker = '',
    customLabels = {},
    customValues = {}
  } = options

  // Build headers
  const headers = buildExportHeaders(exportColumns, columnOrder, customLabels)

  // Build data rows
  const rows = talents.map(talent =>
    buildExportRow(talent, exportColumns, userOptions, columnOrder, { emptyMarker, customValues })
  )

  // Convert to TSV format
  const tsvContent = convertToTSV(headers, rows, includeHeaders)

  // Copy to clipboard
  await navigator.clipboard.writeText(tsvContent)

  return {
    success: true,
    count: talents.length,
    tsvContent
  }
}
